import { GDTF_REGEX } from "@coten-inc/utils/dist/gdtf/parser";
import * as GeoTIFF from "geotiff";
import { FileText, Info, Layers, Plus, Trash2 } from "lucide-react";
import { type ChangeEvent, useEffect, useId, useRef, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedParams } from "react-router-typesafe-routes";
import { v4 as uuidv4 } from "uuid";

import { GdtfDateInput } from "~/components/gdtf-date-input";
import { SectionCard } from "~/components/section-card";
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Textarea,
} from "~/components/ui";
import { useAreaInputContext, useRecordGeoItemCreate } from "~/features/recordGeoItems/hooks";
import { emptyRecordGeoItemForm, type RecordGeoItemFormData } from "~/features/recordGeoItems/types";
import { OverlayMap } from "~/lib/map/components/OverlayMap";
import type { ResolutionType, ViewportType } from "~/lib/map/types";
import { createUrl, type Gdal, getCenterCoords, initGdal } from "~/utils/geotiff";
import { calculateSurroundedH3Indexes } from "~/utils/surroundedH3Indexes";
import { v2Route } from "~/v2/routes";

const MAX_OVERLAY_IMAGES = 5;

interface OverlayImage {
  id: string;
  name: string;
  url: string;
}

/** Create a geo-item by tracing H3 cells over georeferenced GeoTIFF overlays. */
export function RecordGeoItemCreateWithOverlay() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { recordId = "" } = useTypedParams(v2Route.areaInput.record);
  const { data: context } = useAreaInputContext();
  const opacityId = useId();

  const { submit, processing } = useRecordGeoItemCreate({
    recordId,
    onCreated: (geoItemId) => navigate(v2Route.areaInput.record.detail.$buildPath({ params: { recordId, geoItemId } })),
  });

  const {
    register,
    control,
    getValues,
    setValue,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<RecordGeoItemFormData>({ defaultValues: emptyRecordGeoItemForm });
  const evidences = useFieldArray({
    control,
    name: "evidences",
    rules: { required: { value: true, message: t("feature.recordGeoItems.form.errors.requiredEvidences") } },
  });

  const [resolution, setResolution] = useState<ResolutionType>(3);
  const [overlayOpacity, setOverlayOpacity] = useState(0.2);
  const [dragOverlayImageMode, setDragOverlayImageMode] = useState(false);
  const [overlayImages, setOverlayImages] = useState<OverlayImage[]>([]);
  const [mapKey, setMapKey] = useState(() => uuidv4());
  const [initialViewState, setInitialViewState] = useState<Partial<ViewportType> | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // `useWatch` (hook) instead of the `watch()` method so React Compiler can keep memoizing.
  const h3Indexes = useWatch({ control, name: "h3Indexes" });

  const [gdal, setGdal] = useState<Gdal | null>(null);
  useEffect(() => {
    void initGdal().then(setGdal);
  }, []);

  const handleSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !gdal) return;
    const convertedUrl = await createUrl(file, gdal);
    const tiff = await GeoTIFF.fromUrl(convertedUrl);
    const image = await tiff.getImage();
    const { centerLng, centerLat } = await getCenterCoords(await image.getBoundingBox());
    setOverlayImages((prev) => [...prev, { id: uuidv4(), name: file.name, url: convertedUrl }]);
    setInitialViewState({ longitude: centerLng, latitude: centerLat, zoom: 3 });
    setMapKey(uuidv4());
  };

  const toggleHexagon = (h3Index: string) => {
    const current = getValues("h3Indexes");
    setValue("h3Indexes", current.includes(h3Index) ? current.filter((i) => i !== h3Index) : [...current, h3Index]);
  };

  const autoFill = () => {
    const current = getValues("h3Indexes");
    setValue("h3Indexes", Array.from(new Set([...current, ...calculateSurroundedH3Indexes(current)])));
  };

  const onSubmit = (data: RecordGeoItemFormData) => {
    let valid = true;
    if (!data.jaLabel && !data.enLabel) {
      setError("jaLabel", { message: t("ui.required") });
      valid = false;
    }
    if (data.h3Indexes.length === 0) {
      setError("h3Indexes", { message: t("feature.recordGeoItems.form.errors.requiredH3Indexes") });
      valid = false;
    }
    if (!valid) return;
    void submit({ ...data, geometry: null });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <h1 className="text-dewey-title capitalize text-fg-1">
        {t("feature.recordGeoItems.createWithOverlayImg.title")}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <SectionCard icon={Info} title={t("ui.detail")} contentClassName="grid gap-4 sm:grid-cols-2">
          <Field label={t("model.recordGeoItem.i18nLabel.ja")} error={errors.jaLabel?.message}>
            <Input {...register("jaLabel")} />
          </Field>
          <Field label={t("model.recordGeoItem.i18nLabel.en")} error={errors.enLabel?.message}>
            <Input {...register("enLabel")} />
          </Field>
          <Controller
            control={control}
            name="date"
            rules={{
              required: t("ui.required"),
              pattern: { value: GDTF_REGEX, message: t("feature.recordGeoItems.form.errors.invalidGdtfFormat") },
            }}
            render={({ field, fieldState }) => (
              <GdtfDateInput
                label={t("ui.date")}
                value={field.value ?? ""}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="endDate"
            rules={{
              validate: (v) => !v || GDTF_REGEX.test(v) || t("feature.recordGeoItems.form.errors.invalidGdtfFormat"),
            }}
            render={({ field, fieldState }) => (
              <GdtfDateInput
                label={t("model.geoItem.endDate")}
                value={field.value ?? ""}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Field label={t("model.recordGeoItem.memo")} className="sm:col-span-2">
            <Textarea {...register("memo")} className="min-h-[4rem]" />
          </Field>
        </SectionCard>

        <SectionCard
          icon={Layers}
          title={t("feature.recordGeoItems.overlayImages.title")}
          contentClassName="space-y-3"
          action={
            <div className="flex items-center gap-3">
              <Label className="whitespace-nowrap text-12-bold text-fg-2">
                {t("feature.recordGeoItems.form.resolution")}
              </Label>
              <Slider
                min={0}
                max={4}
                step={1}
                value={resolution}
                onChange={(e) => setResolution(Number(e.target.value) as ResolutionType)}
                className="w-32"
                aria-label={t("feature.recordGeoItems.form.resolution")}
              />
              <span className="w-3 text-12-bold tabular-nums text-fg-1">{resolution}</span>
              <Button type="button" variant="outline" size="sm" onClick={autoFill}>
                {t("feature.recordGeoItems.form.autoFill")}
              </Button>
            </div>
          }
        >
          <div className="flex flex-wrap items-center gap-4 rounded-[var(--radius-md)] border border-border-default p-3">
            <div className="flex items-center gap-2">
              <Label htmlFor={opacityId} className="text-12-bold text-fg-2">
                {t("feature.recordGeoItems.overlayImages.opacity")}
              </Label>
              <Slider
                id={opacityId}
                min={0}
                max={1}
                step={0.1}
                value={overlayOpacity}
                onChange={(e) => setOverlayOpacity(Number(e.target.value))}
                className="w-32"
              />
            </div>
            <label htmlFor="overlay-drag-mode" className="flex items-center gap-2 text-fg-1">
              <Checkbox
                id="overlay-drag-mode"
                checked={dragOverlayImageMode}
                onCheckedChange={(c) => setDragOverlayImageMode(c === true)}
              />
              <span className="text-12-bold text-fg-2">{t("feature.recordGeoItems.overlayImages.dragMode")}</span>
            </label>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-12-regular text-fg-3">
                {overlayImages.length} / {MAX_OVERLAY_IMAGES}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".tif,.tiff"
                className="hidden"
                onChange={(e) => void handleSelectFile(e)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!gdal || overlayImages.length >= MAX_OVERLAY_IMAGES}
                onClick={() => fileInputRef.current?.click()}
              >
                {t("feature.recordGeoItems.overlayImages.addImage")}
              </Button>
            </div>
            {overlayImages.length > 0 && (
              <ul className="w-full space-y-1">
                {overlayImages.map((img) => (
                  <li key={img.id} className="flex items-center justify-between gap-2 text-[13px]">
                    <span className="truncate text-fg-1">{img.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      aria-label={t("ui.delete")}
                      onClick={() => setOverlayImages((prev) => prev.filter((i) => i.id !== img.id))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <OverlayMap
            key={mapKey}
            height={560}
            resolution={resolution}
            areaH3Indexes={h3Indexes}
            overlayImageUrls={overlayImages.map((img) => img.url)}
            overlayOpacity={overlayOpacity}
            dragOverlayImageMode={dragOverlayImageMode}
            initialViewState={initialViewState}
            onHexagonClick={toggleHexagon}
          />
          <p className="text-12-regular text-fg-3">
            {t("feature.recordGeoItems.overlayImages.multiSelectModeDescription")}
          </p>
          {errors.h3Indexes && <p className="text-12-medium text-danger">{errors.h3Indexes.message}</p>}
        </SectionCard>

        <SectionCard icon={FileText} title={t("model.evidence.modelNames")} contentClassName="space-y-3">
          {errors.evidences?.root && <p className="text-12-medium text-danger">{errors.evidences.root.message}</p>}
          {evidences.fields.map((field, index) => {
            const refError = errors.evidences?.[index]?.reference?.message;
            const pageError = errors.evidences?.[index]?.citation?.page?.message;
            return (
              <div key={field.id} className="flex items-start gap-2">
                <div className="flex-1">
                  <Controller
                    control={control}
                    name={`evidences.${index}.reference`}
                    rules={{ required: t("ui.required") }}
                    render={({ field: refField }) => (
                      <Select
                        value={refField.value?.id ?? ""}
                        onValueChange={(id) => {
                          const ref = context.references.find((r) => r.id === id);
                          refField.onChange(ref ? { id: ref.id, title: ref.title } : null);
                        }}
                      >
                        <SelectTrigger
                          className="w-full"
                          aria-label={t("model.reference.modelName")}
                          aria-invalid={!!refError}
                        >
                          <SelectValue placeholder={t("model.reference.modelName")} />
                        </SelectTrigger>
                        <SelectContent>
                          {context.references.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.shortCode || r.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {refError && <p className="mt-1 text-12-medium text-danger">{refError}</p>}
                </div>
                <div className="w-32">
                  <Input
                    {...register(`evidences.${index}.citation.page`, {
                      required: t("ui.required"),
                      pattern: { value: /^\d+$/, message: t("ui.numberOnly") },
                    })}
                    inputMode="numeric"
                    placeholder={t("model.evidence.page")}
                    aria-invalid={!!pageError}
                  />
                  {pageError && <p className="mt-1 text-12-medium text-danger">{pageError}</p>}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("ui.delete")}
                  onClick={() => evidences.remove(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => evidences.append({ reference: null, citation: { page: "" } })}
          >
            <Plus />
            <span>{t("ui.add")}</span>
          </Button>
        </SectionCard>

        <div className="flex items-center justify-between border-t border-border-default pt-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={processing}>
            {t("ui.cancel")}
          </Button>
          <Button type="submit" disabled={processing}>
            {t("ui.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <Label className="text-12-bold capitalize text-fg-2">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-12-medium text-danger">{error}</p>}
    </div>
  );
}
